import { Component } from '@theme/component';

/**
 * @typedef {{ frame?: HTMLElement, zoom?: HTMLDetailsElement,
 *   caption?: HTMLElement, links?: HTMLAnchorElement[], video?: HTMLVideoElement,
 *   videoPanel?: HTMLElement, videoError?: HTMLElement, errorPoster?: HTMLImageElement,
 *   videoFallback?: HTMLAnchorElement }} GalleryRefs
 * @extends {Component<GalleryRefs>}
 */
class RWProductGallery extends Component {
  #events;
  #activeSource = '';
  #sizeObserver;
  #stickyTarget;

  connectedCallback() {
    super.connectedCallback();
    this.#events?.abort();
    this.#events = new AbortController();
    this.#stickyTarget = this.closest('.product-information__media') || this.closest('.rw-product-figure');
    this.#sizeObserver = new ResizeObserver(() => this.#fitStickyGallery());
    const figure = this.closest('.rw-product-figure');
    if (figure) this.#sizeObserver.observe(figure);
    const header = document.querySelector('#header-group');
    if (header) this.#sizeObserver.observe(header);
    window.addEventListener('resize', () => {
      this.#placeMobileOffer();
      this.#fitStickyGallery();
    }, { signal: this.#events.signal });
    document.addEventListener('DOMContentLoaded', () => this.#placeMobileOffer(), { once: true, signal: this.#events.signal });
    this.#placeMobileOffer();
    this.addEventListener('toggle', () => this.#fitStickyGallery(), { capture: true, signal: this.#events.signal });
    this.#fitStickyGallery();
    this.refs.video?.addEventListener('error', () => this.showVideoError(), { signal: this.#events.signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#placeMobileOffer();
    this.#fitStickyGallery();
  }

  #placeMobileOffer() {
    this.rwMobileOffer?.place();
  }

  #showSelectedMedia() {
    if (!this.hasAttribute('data-mobile-offer')) return;
    const target = this.hasAttribute('data-video-active') ? this.refs.video : this.refs.zoom?.querySelector('summary');
    target?.focus({ preventScroll: true });
    this.querySelector('.rw-gallery-stage')?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  #fitStickyGallery() {
    this.#placeMobileOffer();
    const target = this.#stickyTarget;
    if (!target) return;
    const header = document.querySelector('#header-group');
    const offset = Math.ceil(header?.getBoundingClientRect().height || 80) + 16;
    const fits = innerWidth >= 750 && (this.closest('.rw-product-figure')?.getBoundingClientRect().height || Infinity) <= innerHeight - offset - 16;
    target.style.setProperty('--rw-gallery-top', `${offset}px`);
    target.toggleAttribute('data-rw-gallery-sticky', fits && !this.refs.zoom?.open);
  }

  showVideoError() {
    const { video, videoError } = this.refs;
    if (!this.hasAttribute('data-video-active') || !video?.error || !videoError) return;
    if (video.currentSrc && video.currentSrc !== this.#activeSource) return;
    video.pause();
    video.hidden = true;
    videoError.hidden = false;
    if (document.activeElement === video) this.refs.videoFallback?.focus();
  }

  /** @param {MouseEvent} event */
  selectImage(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-src]') : null;
    const image = this.refs.frame?.querySelector('img');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link) || !(image instanceof HTMLImageElement)) return;
    if (!link.dataset.src || !link.dataset.srcset) return;

    event.preventDefault();
    this.refs.video?.pause();
    this.#activeSource = '';
    if (this.refs.videoError) this.refs.videoError.hidden = true;
    if (this.refs.videoPanel) this.refs.videoPanel.hidden = true;
    this.removeAttribute('data-video-active');
    if (this.refs.zoom) this.refs.zoom.inert = false;
    if (this.refs.zoom instanceof HTMLDetailsElement) this.refs.zoom.open = false;
    image.srcset = link.dataset.srcset;
    image.src = link.dataset.src;
    image.alt = link.dataset.alt || '';
    this.updateSelection(link);
    this.#showSelectedMedia();
  }

  /** @param {MouseEvent} event */
  selectVideo(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-video-src]') : null;
    const { video, videoPanel, zoom } = this.refs;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link) || !link.dataset.videoSrc) return;
    if (!(video instanceof HTMLVideoElement) || !videoPanel || !zoom) return;
    event.preventDefault();
    zoom.open = false;
    zoom.inert = true;
    this.setAttribute('data-video-active', '');
    videoPanel.hidden = false;
    video.hidden = false;
    if (this.refs.videoError) this.refs.videoError.hidden = true;
    this.#activeSource = new URL(link.dataset.videoSrc, document.baseURI).href;
    if (this.refs.errorPoster) this.refs.errorPoster.src = link.dataset.videoPoster || '';
    if (this.refs.videoFallback) this.refs.videoFallback.href = link.href;
    video.poster = link.dataset.videoPoster || '';
    video.setAttribute('aria-label', link.getAttribute('aria-label') || '');
    video.textContent = link.dataset.caption || '';
    // No video download until requested, and playback always uses native controls.
    if (video.getAttribute('src') !== link.dataset.videoSrc || video.error) {
      video.pause();
      video.preload = 'metadata';
      video.src = link.dataset.videoSrc;
    }
    this.updateSelection(link);
    this.#showSelectedMedia();
  }

  /** @param {HTMLAnchorElement} link */
  updateSelection(link) {
    if (this.refs.caption instanceof HTMLElement) this.refs.caption.textContent = link.dataset.caption || '';
    for (const thumbnail of this.refs.links || []) {
      if (thumbnail === link) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    }
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#sizeObserver?.disconnect();
    this.rwMobileOffer?.disconnect();
    this.#stickyTarget?.removeAttribute('data-rw-gallery-sticky');
    this.refs.video?.pause();
    super.disconnectedCallback();
  }
}

if (!customElements.get('rw-product-gallery')) customElements.define('rw-product-gallery', RWProductGallery);
